import type { FeeItem, Holding, MeWallet, PositionListItem, RouterOutputs } from './api-types'
import { formatTokenAmount, formatUsd } from './format'
import { SOL_MINT, USD_LOGO, USDC_MINT, USDT_MINT, tokenLogo } from './default-tokens'
import { CUSTODY_LABELS } from './pool'
import type { Platform } from '@/components/custody-logo'

export type ActivityKind = 'deposit' | 'send' | 'swap'

export type DepositItem = RouterOutputs['deposit']['list']['items'][number]
export type TransferItem = RouterOutputs['transfer']['list']['items'][number]
export type HistoryItem = RouterOutputs['swap']['list']['items'][number]
export type PrivateDepositItem = RouterOutputs['privateDeposit']['list'][number]
export type PrivateWithdrawalItem = RouterOutputs['privateWithdrawal']['list'][number]
export type VaultDepositItem = RouterOutputs['vault']['deposits'][number]
export type VaultWithdrawalItem = RouterOutputs['vault']['withdrawals'][number]
export type KrakenOrderItem = RouterOutputs['kraken']['orders'][number]
export type KrakenWithdrawalItem = RouterOutputs['kraken']['withdrawals'][number]

/** one side of a row: how much of which token (native SOL under SOL_MINT) */
export type ActivityLeg = { amount: number; symbol: string; address: string }

/** One deposit, withdrawal or swap, the way the Transactions table and the reports see it. */
export type ActivityRow = {
  id: string
  kind: ActivityKind
  /** Deposit, Withdraw, Buy or Sell */
  type: string
  symbol: string
  imageUrl?: string
  /** what left the wallet, formatted */
  paid?: string
  /** what arrived, formatted */
  received: string
  /** the legs as numbers, for reports and totals */
  paidLeg?: ActivityLeg
  receivedLeg?: ActivityLeg
  valueUsd?: number
  /** what the flow paid, line by line; null until it is known */
  fees?: FeeItem[] | null
  counterparty?: string
  counterpartyLabel?: string
  /** the direction alone, for the all tab's From / To column */
  counterpartyDirection?: 'From' | 'To'
  /** the place behind the address, shown as its mark (John, Nov 1 2026: the logo, not the name): an exchange, Kraken or the vault */
  counterpartyPlace?: string
  isPrivate?: boolean
  status: string
  createdAt?: string
  explorerUrl?: string
  /** the on-chain signature, in full */
  txId?: string
  /** the automated strategy whose order this swap was; absent for a hand trade */
  strategyId?: string
  /** the position wallet a swap ran in; absent for the trading wallet's own */
  positionId?: string
  /** where it happened (John, Oct 31 2026): the trading wallet itself, Sonar's pool, or an exchange */
  platform: Platform
  searchText: string
}

export type ActivitySources = {
  wallet: MeWallet
  /** exchange custody: the trading wallet's own lists stay out, only that custody's records show */
  exchange: boolean
  /** where the account's funds are held (Sonar, MEXC, ...), for a deposit that went on there */
  custodyLabel: string
  /** today's prices by mint (native SOL under SOL_MINT), for rows whose record carries no dollar value */
  prices: Map<string, number>
  deposits: DepositItem[]
  transfers: TransferItem[]
  swaps: HistoryItem[]
  privateDeposits: PrivateDepositItem[]
  privateWithdrawals: PrivateWithdrawalItem[]
  positions: PositionListItem[]
  vaultDeposits: VaultDepositItem[]
  vaultWithdrawals: VaultWithdrawalItem[]
  /** sales for USD on Kraken's order book (Nov 1 2026) */
  krakenOrders?: KrakenOrderItem[]
  /** USD sent from Kraken to a bank (Nov 2 2026) */
  krakenWithdrawals?: KrakenWithdrawalItem[]
}

/** today's prices from the holdings; native SOL is keyed by the mint the rows use */
export function pricesFromHoldings(holdings: Holding[]): Map<string, number> {
  const map = new Map<string, number>()
  for (const h of holdings) {
    if (!h.priceUsd) continue
    // only Solana's native row is SOL; the other chains' native rows (ETH, BNB) are not priced here
    const key = h.isNative ? (h.chain === 'solana' ? SOL_MINT : null) : h.address
    if (key === null) continue
    // the native row wins over a wrapped-SOL token account at the same mint
    if (h.isNative || !map.has(key)) map.set(key, h.priceUsd)
  }
  return map
}

const leg = (amount: number, symbol: string, address: string): ActivityLeg => ({ amount, symbol, address })
/** the mint behind each asset Kraken sells, for the row's logo and the reports */
const KRAKEN_MINTS: Record<string, string> = { SOL: SOL_MINT, USDC: USDC_MINT, USDT: USDT_MINT }
/** Kraken's fee on a sale as the one line of its receipt, in USD cents; nothing when there was none */
export function krakenFeeItems(feeUsd: number): FeeItem[] {
  return feeUsd > 0 ? [{ label: 'Kraken fee', symbol: 'USD', mint: null, decimals: 2, amountRaw: String(Math.round(feeUsd * 100)), usd: feeUsd }] : []
}
/** the name of a place money moves between: an exchange, Sonar, or the account's vault */
const placeLabel = (id: string | null | undefined) => (id === 'vault' ? 'Vault' : ((CUSTODY_LABELS as Record<string, string>)[id ?? ''] ?? (id ?? '')))
const str = (value: string | null | undefined) => value ?? undefined
/** a vault record keeps the instruction index after the signature ("sig:0"); the explorer and the reports want the signature alone */
const signature = (value: string | null | undefined) => (value ? value.replace(/:\d+$/, '') : undefined)

/**
 * Every deposit, withdrawal and swap of the wallet as one list, newest
 * first. Internal legs (a sell's return, a deposit's own routing, moves
 * between the account's own numbers) never become rows of their own. The
 * Transactions table renders these; the reports export the same rows.
 */
export function buildActivityRows(s: ActivitySources): ActivityRow[] {
  const { wallet, prices } = s
  // every platform at all times (John, Oct 31 2026): each row names where it happened
  const deposits = s.deposits
  const transfers = s.transfers
  const swaps = s.swaps
  const privateDeposits = s.privateDeposits
  const privateWithdrawals = s.privateWithdrawals
  const out: ActivityRow[] = []
  const usdOf = (mint: string | null | undefined, amountUi: number) => {
    // a stablecoin is a dollar even when nothing in the holdings prices it
    const price = prices.get(mint ?? SOL_MINT) ?? (mint === USDC_MINT || mint === USDT_MINT ? 1 : undefined)
    return price !== undefined ? amountUi * price : undefined
  }
  // what lands in the wallet from a sell or a deposit's own routing is part of that row, not a deposit of its own
  const internalTx = new Set<string>()
  for (const p of s.positions) for (const r of p.returns) if (r.fillTxHash) internalTx.add(r.fillTxHash)
  for (const d of privateDeposits) {
    if (d.fill?.txHash) internalTx.add(d.fill.txHash)
    for (const c of d.sweeps) if (c.fillTxHash) internalTx.add(c.fillTxHash)
  }
  for (const d of privateDeposits) {
    if (d.walletAddress !== wallet.solanaAddress || d.status === 'awaitingFunds' || !d.fundedLamports) continue
    const symbol = d.asset.symbol.toUpperCase()
    const raw = d.fill?.receivedLamports ?? d.fundedLamports
    const amountUi = Number(raw) / 10 ** d.asset.decimals
    const tx = d.asset.kind === 'token' ? d.forward?.signature : d.fill?.txHash
    const address = d.asset.mint ?? SOL_MINT
    out.push({
      id: `pd-${d.id}-${d.createdAt}`,
      kind: 'deposit',
      type: 'Deposit',
      symbol,
      imageUrl: tokenLogo(address, d.asset.imageUrl ?? undefined),
      received: `${formatTokenAmount(amountUi, symbol, 6)} ${symbol}`,
      receivedLeg: leg(amountUi, symbol, address),
      valueUsd: d.amountUsd ?? usdOf(d.asset.mint, amountUi),
      fees: d.fees,
      status: d.status === 'completed' ? 'completed' : d.status === 'failed' ? 'failed' : 'pending',
      createdAt: d.completedAt ?? d.createdAt ?? undefined,
      explorerUrl: tx ? `https://solscan.io/tx/${tx}` : undefined,
      txId: str(tx),
      platform: 'wallet',
      searchText: `deposit ${symbol} ${d.depositAddress} ${tx ?? ''}`,
    })
  }
  for (const d of deposits) {
    if (d.chain !== 'solana') continue
    if (d.transactionId && internalTx.has(d.transactionId)) continue
    const symbol = (d.symbol ?? 'SOL').toUpperCase()
    // native deposits carry a sentinel address, not the wrapped mint
    const address = symbol === 'SOL' ? SOL_MINT : d.tokenAddress
    const amountUi = d.amountUi ?? 0
    out.push({
      id: `d-${d.depositId}`,
      kind: 'deposit',
      type: 'Deposit',
      symbol,
      imageUrl: tokenLogo(d.tokenAddress) ?? (symbol === 'SOL' ? tokenLogo(SOL_MINT) : undefined),
      received: `${formatTokenAmount(amountUi, symbol, 6)} ${symbol}`,
      receivedLeg: leg(amountUi, symbol, address),
      valueUsd: d.amountUsd ?? (symbol === 'SOL' ? usdOf(SOL_MINT, amountUi) : undefined),
      counterparty: d.fromAddress ?? undefined,
      counterpartyLabel: 'From',
      counterpartyDirection: 'From',
      counterpartyPlace: 'wallet',
      status: 'completed',
      createdAt: d.createdAt ?? undefined,
      explorerUrl: d.explorerUrl ?? undefined,
      txId: str(d.transactionId),
      platform: 'wallet',
      searchText: `deposit ${symbol} ${d.fromAddress ?? ''} ${d.explorerUrl ?? ''}`,
    })
  }
  for (const t of transfers) {
    if (t.chain !== 'solana') continue
    const symbol = (t.symbol ?? '').toUpperCase()
    const amountUi = t.amountUi ?? 0
    const address = t.tokenAddress ?? (symbol === 'SOL' ? SOL_MINT : symbol)
    out.push({
      id: `t-${t.transferId}`,
      kind: 'send',
      type: 'Withdraw',
      symbol,
      imageUrl: t.imageUrl ?? (symbol === 'SOL' ? tokenLogo(SOL_MINT) : undefined),
      paid: `${formatTokenAmount(amountUi, symbol, 6)} ${symbol}`,
      paidLeg: leg(amountUi, symbol, address),
      received: '',
      valueUsd: t.amountUsd ?? (symbol === 'SOL' ? usdOf(SOL_MINT, amountUi) : undefined),
      fees: t.fees,
      counterparty: t.toAddress,
      counterpartyLabel: 'To',
      counterpartyDirection: 'To',
      counterpartyPlace: 'wallet',
      status: t.status,
      createdAt: t.createdAt ?? undefined,
      explorerUrl: t.explorerUrl ?? undefined,
      txId: str(t.transactionId),
      platform: 'wallet',
      searchText: `withdraw send ${symbol} ${t.toAddress} ${t.transactionId ?? ''}`,
    })
  }
  for (const d of s.vaultDeposits) {
    // a buy's hand-over to custody is part of the buy, not a transaction of its own; an address nothing reached yet is not one either
    // a move between two of the account's own numbers is housekeeping, not a transaction either
    if (d.walletAddress !== wallet.solanaAddress || d.source === 'custody' || d.source === 'spread' || d.status === 'awaitingFunds') continue
    const symbol = (d.tokenSymbol ?? '').toUpperCase()
    const amountUi = d.sonarAmount ? Number(d.sonarAmount) / 10 ** (d.tokenDecimals ?? 0) : undefined
    out.push({
      id: `vd-${d.id}`,
      kind: 'deposit',
      type: 'Deposit',
      symbol,
      imageUrl: tokenLogo(d.tokenAddress, d.tokenImageUrl ?? undefined),
      received: amountUi !== undefined ? `${formatTokenAmount(amountUi, symbol, 6)} ${symbol}` : symbol,
      receivedLeg: amountUi !== undefined ? leg(amountUi, symbol, d.tokenAddress) : undefined,
      valueUsd: amountUi !== undefined ? usdOf(d.tokenAddress, amountUi) : undefined,
      fees: d.fees,
      counterparty: d.depositAddress,
      // a deposit through its one-time address went on to the exchange, not the vault (John, Oct 31 2026)
      // a transfer (Nov 1 2026) came from another of the account's places; anything else came from an outside wallet,
      // shown as the generic wallet glyph (John, Nov 1 2026: the vault's tile read as a sender called "dispence"); where
      // the money then went (the exchange, the vault) is the Platform column's
      counterpartyLabel: d.source === 'transfer' ? `From ${placeLabel(d.fromPlatform)}` : 'From',
      counterpartyDirection: 'From',
      counterpartyPlace: d.source === 'transfer' ? (d.fromPlatform ?? 'wallet') : 'wallet',
      isPrivate: true,
      status: d.status === 'credited' ? 'completed' : d.status === 'failed' || d.status === 'cancelled' ? 'failed' : 'pending',
      createdAt: d.createdAt ?? undefined,
      explorerUrl: d.transactionHash ? `https://solscan.io/tx/${signature(d.transactionHash)}` : undefined,
      txId: signature(d.transactionHash),
      platform: d.platform,
      searchText: `deposit vault ${symbol} ${d.tokenAddress} ${d.depositAddress} ${d.transactionHash ?? ''}`,
    })
  }
  for (const w of s.vaultWithdrawals) {
    // a sell's collection from custody is part of the sell, not a transaction of its own
    if (w.walletAddress !== wallet.solanaAddress || w.purpose === 'position' || w.purpose === 'season') continue
    const symbol = (w.tokenSymbol ?? '').toUpperCase()
    const amountUi = Number(w.amountRaw) / 10 ** w.tokenDecimals
    const firstTx = w.parts.find((p) => p.transactionHash)?.transactionHash
    out.push({
      id: `vw-${w.id}`,
      kind: 'send',
      type: 'Withdraw',
      symbol,
      imageUrl: tokenLogo(w.tokenAddress, w.tokenImageUrl ?? undefined),
      paid: `${formatTokenAmount(amountUi, symbol, 6)} ${symbol}`,
      paidLeg: leg(amountUi, symbol, w.tokenAddress),
      received: '',
      valueUsd: usdOf(w.tokenAddress, amountUi),
      fees: w.fees,
      counterparty: w.toAddress,
      counterpartyLabel: w.toLabel ? `To ${w.toLabel}` : 'To',
      counterpartyDirection: 'To',
      counterpartyPlace: w.toPlace ?? 'wallet',
      isPrivate: true,
      // on chain but not yet credited at the far end (an exchange, Kraken) is still pending (John, Nov 2 2026)
      status: w.status === 'completed' ? (w.transfer && w.transfer.status !== 'credited' && w.transfer.status !== 'failed' ? 'pending' : 'completed') : w.status === 'failed' ? 'failed' : w.status === 'stalled' ? 'stalled' : 'pending',
      createdAt: w.createdAt ?? undefined,
      explorerUrl: firstTx ? `https://solscan.io/tx/${signature(firstTx)}` : undefined,
      txId: signature(firstTx),
      platform: w.platform,
      searchText: `withdraw vault ${symbol} ${w.tokenAddress} ${w.toAddress}`,
    })
  }
  for (const w of privateWithdrawals) {
    if (w.walletAddress !== wallet.solanaAddress) continue
    const amountUi = Number(w.paidLamports ?? w.amountLamports) / 10 ** w.asset.decimals
    const address = w.asset.mint ?? SOL_MINT
    out.push({
      id: `pw-${w.id}`,
      kind: 'send',
      type: 'Withdraw',
      symbol: w.asset.symbol,
      imageUrl: tokenLogo(address),
      paid: `${formatTokenAmount(amountUi, w.asset.symbol, 6)} ${w.asset.symbol}`,
      paidLeg: leg(amountUi, w.asset.symbol, address),
      received: '',
      valueUsd: w.amountUsd ?? usdOf(w.asset.mint, amountUi),
      fees: w.fees,
      counterparty: w.toAddress,
      counterpartyLabel: 'To',
      counterpartyDirection: 'To',
      counterpartyPlace: 'wallet',
      isPrivate: true,
      status: w.status === 'completed' ? 'completed' : w.status === 'failed' ? 'failed' : w.status === 'stalled' ? 'stalled' : 'pending',
      createdAt: w.createdAt ?? undefined,
      explorerUrl: w.explorerUrl ?? undefined,
      txId: str(w.payoutSignature),
      platform: 'wallet',
      searchText: `withdraw send private ${w.asset.symbol} ${w.toAddress} ${w.payoutSignature ?? ''}`,
    })
  }
  for (const o of s.krakenOrders ?? []) {
    // a check against Kraken that sold nothing (a local run) is no transaction
    if (o.outcome === 'checked') continue
    const sold = o.volumeExecuted > 0 ? o.volumeExecuted : o.volume
    const mint = KRAKEN_MINTS[o.asset] ?? SOL_MINT
    const paidOut = o.costUsd > 0
    out.push({
      id: `k-${o.id}`,
      kind: 'swap',
      type: 'Sell',
      symbol: o.asset,
      imageUrl: tokenLogo(mint),
      paid: `${formatTokenAmount(sold, o.asset)} ${o.asset}`,
      paidLeg: leg(sold, o.asset, mint),
      received: paidOut ? formatUsd(o.netUsd) : o.outcome === 'placing' ? '…' : '',
      receivedLeg: paidOut ? leg(o.netUsd, 'USD', 'USD') : undefined,
      valueUsd: paidOut ? o.costUsd : usdOf(mint, sold),
      fees: krakenFeeItems(o.feeUsd),
      status: o.outcome === 'filled' || o.outcome === 'partial' ? 'completed' : o.outcome === 'failed' ? 'failed' : o.outcome === 'unfilled' ? 'cancelled' : 'pending',
      createdAt: o.createdAt ?? undefined,
      txId: o.txid ?? undefined,
      platform: 'kraken',
      searchText: `sell kraken ${o.asset} usd ${o.txid ?? ''}`,
    })
  }
  for (const w of s.krakenWithdrawals ?? []) {
    // a local run that sent nothing is no transaction
    if (w.status === 'checked') continue
    out.push({
      id: `kw-${w.id}`,
      kind: 'send',
      type: 'Withdraw',
      symbol: 'USD',
      imageUrl: USD_LOGO,
      paid: formatUsd(w.amountUsd),
      paidLeg: leg(w.amountUsd, 'USD', 'USD'),
      received: '',
      valueUsd: w.amountUsd,
      fees: krakenFeeItems(w.feeUsd),
      // the bank, by the name Kraken knows it under
      counterparty: w.destinationKey,
      counterpartyLabel: `To ${w.destinationKey}`,
      counterpartyDirection: 'To',
      counterpartyPlace: `bank:${w.destinationKey}`,
      status: w.status === 'completed' ? 'completed' : w.status === 'failed' ? 'failed' : 'pending',
      createdAt: w.createdAt ?? undefined,
      platform: 'kraken',
      searchText: `withdraw kraken usd bank ${w.destinationKey} ${w.refid ?? ''}`,
    })
  }
  for (const h of swaps) {
    if (h.kind !== 'swap' || h.chain !== 'solana') continue
    const symbol = (h.tokenSymbol ?? '').toUpperCase()
    const inSym = (h.inputSymbol ?? '').toUpperCase()
    const outSym = (h.outputSymbol ?? '').toUpperCase()
    // the traded token against SOL, or the stablecoin the record settled in (Nov 1 2026: three USDC sales read as SOL)
    const buy = h.side === 'buy'
    const settlementMint = (mint: string | undefined) => (mint === USDC_MINT || mint === USDT_MINT ? mint : SOL_MINT)
    const paidAddress = buy ? settlementMint(h.inputTokenAddress) : h.tokenAddress
    const receivedAddress = buy ? h.tokenAddress : settlementMint(h.outputTokenAddress)
    out.push({
      id: `s-${h.swapId}`,
      kind: 'swap',
      type: buy ? 'Buy' : 'Sell',
      symbol,
      imageUrl: h.imageUrl ?? undefined,
      paid: `${formatTokenAmount(h.inputAmountUi ?? 0, inSym)} ${inSym}`,
      paidLeg: leg(h.inputAmountUi ?? 0, inSym, paidAddress),
      received: h.outputAmountUi !== undefined ? `${formatTokenAmount(h.outputAmountUi, outSym)} ${outSym}` : '…',
      receivedLeg: h.outputAmountUi !== undefined ? leg(h.outputAmountUi, outSym, receivedAddress) : undefined,
      valueUsd: h.outputAmountUsd ?? h.inputAmountUsd ?? undefined,
      fees: h.fees,
      isPrivate: h.isPrivate,
      status: h.status,
      createdAt: h.createdAt ?? undefined,
      explorerUrl: h.transactionId ? `https://solscan.io/tx/${h.transactionId}` : undefined,
      txId: str(h.transactionId),
      strategyId: h.strategyId ?? undefined,
      positionId: h.positionId ?? undefined,
      platform: h.platform,
      searchText: `${h.side} swap ${symbol} ${inSym} ${outSym} ${h.transactionId ?? ''} ${h.isPrivate ? 'private' : ''}`,
    })
  }
  out.sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
  return out
}
